import React, { useState, useCallback, useRef, useEffect } from "react"
import { Handle, Position } from "reactflow"
import "./Node.css"
import "./FormNode.css"
import { 
  FileText, 
  Minus,
  Plus,
  MoreHorizontal, 
  Table2,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Loader,
  Plus as PlusIcon,
  Trash2,
  GripVertical,
  ChevronUp as ArrowUp,
  ChevronDown as ArrowDown
} from "lucide-react"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import VariableReference from "./VariableReference"
import EnhancedVariableReference from "./EnhancedVariableReference"
import FormDateExpressionEditor from "./FormDateExpressionEditor"
import { DEFAULT_DYNAMIC_DATE_FORMAT } from "@/lib/dateUtils"
import { normalizeDateOffset } from "@/lib/formDateFieldConfig"
import CopyPasteButtons from "./CopyPasteButtons"
import { DndContext, closestCenter } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useSortableConfig } from '@/hooks/use-sortable-config'
import {
  conditionalBehaviorWithVisibilityMode,
  conditionalVisibilityMode,
  normalizeConditionalBehavior,
} from "@/lib/formFieldConditions"
import { getConditionalMatchOptionsFromFields } from "@/lib/workflowFormRuntime"
import { useWorkflowGraphForVariableDiscovery } from "@/hooks/useWorkflowGraphForVariableDiscovery"

// Sortable wrapper component for draggable form fields
function SortableFieldItem({ field, index, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  )
}

const toSnakeCase = (input) => {
  const text = input === null || input === undefined ? "" : String(input)
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
}

const ensureFormString = (value) =>
  value === null || value === undefined ? "" : String(value)

function mergeFieldUpdates(prevFormData, fieldId, updates) {
  const processedUpdates = { ...updates }

  if (processedUpdates.label !== undefined) {
    processedUpdates.label = ensureFormString(processedUpdates.label)
  }
  if (processedUpdates.placeholder !== undefined) {
    processedUpdates.placeholder = ensureFormString(processedUpdates.placeholder)
  }
  if (processedUpdates.defaultValue !== undefined) {
    processedUpdates.defaultValue = ensureFormString(processedUpdates.defaultValue)
  }
  if (processedUpdates.validation?.pattern !== undefined) {
    processedUpdates.validation = {
      ...processedUpdates.validation,
      pattern: ensureFormString(processedUpdates.validation.pattern),
    }
  }

  const field = prevFormData.fields.find((f) => f.id === fieldId)
  if (field && field.type === "number" && processedUpdates.validation?.min !== undefined) {
    const newMin = processedUpdates.validation.min
    const currentDefault = field.defaultValue
    if (newMin !== undefined && !currentDefault) {
      processedUpdates.defaultValue = newMin.toString()
    }
  }

  return {
    ...prevFormData,
    fields: prevFormData.fields.map((f) => {
      if (f.id !== fieldId) return f
      const next = { ...f, ...processedUpdates }
      if (
        Object.prototype.hasOwnProperty.call(processedUpdates, "defaultValueOffset") &&
        !processedUpdates.defaultValueOffset
      ) {
        delete next.defaultValueOffset
      }
      return next
    }),
  }
}

const FormNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, workflows, deleteNode, updateNode } = useWorkflow()
  const { nodes, edges } = useWorkflowGraphForVariableDiscovery(currentWorkflow?.id)
  
  const { expanded = true } = data
  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [showSquareTooltip, setShowSquareTooltip] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [activeTab, setActiveTab] = useState("form")
  const [expandedFields, setExpandedFields] = useState(new Set()) // Track which fields are expanded
  const minInputRefs = useRef({}) // Refs for min/max inputs to control them directly
  const debounceTimeoutRef = useRef(null) // Debounce timeout for input changes
  const lastInputValues = useRef({}) // Track last input values to prevent duplicate events
  const isProcessingArrowKey = useRef(false) // Flag to prevent arrow key processing loops
  const arrowKeyTimeoutRef = useRef(null) // Timeout for arrow key processing
  const isArrowKeyPressed = useRef(false) // Flag to track if arrow key is currently pressed
  const globalKeyListenerRef = useRef(null) // Reference to global key listener
  const debouncedUpdateFieldRef = useRef(null) // Reference to current debouncedUpdateField function
  const formDataRef = useRef(null)

  // Form data state
  const [formData, setFormData] = useState(() => {
    // Helper function to ensure string values are never null
    const ensureString = (value) => value === null || value === undefined ? "" : String(value)
    
    // Helper function to process fields and ensure all string values are properly set
    const processFields = (fields) => {
      if (!Array.isArray(fields)) return []
      
      return fields.map(field => {
        const defaultValueOffset = normalizeDateOffset(field.defaultValueOffset)
        const validation = {
          minLength: field.validation?.minLength || 0,
          maxLength: field.validation?.maxLength || 100,
          pattern: ensureString(field.validation?.pattern),
          min: field.validation?.min !== undefined ? field.validation.min : undefined,
          max: field.validation?.max !== undefined ? field.validation.max : undefined,
          step: field.validation?.step || 1,
        }
        if (field.validation?.minDate != null && String(field.validation.minDate).trim() !== "") {
          validation.minDate = ensureString(field.validation.minDate)
        }
        if (field.validation?.maxDate != null && String(field.validation.maxDate).trim() !== "") {
          validation.maxDate = ensureString(field.validation.maxDate)
        }
        const minDateOffset = normalizeDateOffset(field.validation?.minDateOffset)
        const maxDateOffset = normalizeDateOffset(field.validation?.maxDateOffset)
        if (minDateOffset) validation.minDateOffset = minDateOffset
        if (maxDateOffset) validation.maxDateOffset = maxDateOffset

        const next = {
          id: field.id || `field-${Date.now()}`,
          type: field.type || "text",
          label: ensureString(field.label) || "New Field",
          placeholder: ensureString(field.placeholder) || "Enter value",
          required: Boolean(field.required),
          defaultValue: ensureString(field.defaultValue),
          validation,
          options: Array.isArray(field.options) ? field.options.map(option => ({
            id: option.id || `option-${Date.now()}`,
            label: ensureString(option.label) || "New Option",
            value: ensureString(option.value) || "new_option",
            valueManuallyEdited: option.valueManuallyEdited !== undefined
              ? Boolean(option.valueManuallyEdited)
              : (ensureString(option.value) !== "" && ensureString(option.value) !== toSnakeCase(ensureString(option.label)))
          })) : [],
          conditionalBehavior: normalizeConditionalBehavior(field.conditionalBehavior),
        }
        if (defaultValueOffset) {
          next.defaultValueOffset = defaultValueOffset
        }
        return next
      })
    }
    
    // Helper function to process settings and ensure all string values are properly set
    const processSettings = (settings) => ({
      submitButtonText: ensureString(settings?.submitButtonText) || "Submit",
      resetButtonText: ensureString(settings?.resetButtonText) || "Reset",
      showProgressBar: Boolean(settings?.showProgressBar),
      allowMultipleSubmissions: Boolean(settings?.allowMultipleSubmissions),
      redirectUrl: ensureString(settings?.redirectUrl),
      successMessage: ensureString(settings?.successMessage) || "Form submitted successfully!",
      errorMessage: ensureString(settings?.errorMessage) || "Please check your input and try again.",
      calculateNumberFields: Boolean(settings?.calculateNumberFields),
      totalCalculationName: ensureString(settings?.totalCalculationName) || "Total Calculation"
    })
    
    return {
      name: ensureString(data.name) || "Dynamic Form",
      description: ensureString(data.description),
      fields: processFields(data.fields),
      settings: processSettings(data.settings),
      dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
    }
  })

  formDataRef.current = formData

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      dynamicDateFormat: data.dynamicDateFormat ?? DEFAULT_DYNAMIC_DATE_FORMAT,
    }))
  }, [data.dynamicDateFormat, id])

  // Setup drag-and-drop sensors with 8px activation distance to prevent accidental drags
  const sensors = useSortableConfig(8)

  // Get available variables from all connected request nodes throughout the workflow
  const getAvailableVariables = () => {
    if (!nodes || !edges) return []
    
    const availableVariables = []
    const visitedNodes = new Set()
    
    // Recursive function to find all upstream request nodes
    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)
      
      const node = nodes.find(n => n.id === nodeId)
      if (!node) return
      
      // If this is a request node, add its extracted variables
      if (node.type === 'requestNode' && node.data.extractedVariables) {
        availableVariables.push(...node.data.extractedVariables)
      }
      
      // Find all incoming edges to this node
      const incomingEdges = edges.filter(edge => edge.target === nodeId)
      
      // Recursively check all source nodes
      incomingEdges.forEach(edge => {
        findUpstreamRequestNodes(edge.source)
      })
    }
    
    // Start from the current form node
    findUpstreamRequestNodes(id)
    
    return availableVariables
  }

  // Get connected request nodes for display
  const getConnectedRequestNodes = () => {
    const connectedRequests = []
    const incomingEdges = edges.filter(edge => edge.target === id)
    
    incomingEdges.forEach(edge => {
      const sourceNode = nodes.find(node => node.id === edge.source)
      if (sourceNode && sourceNode.type === 'requestNode') {
        connectedRequests.push({
          id: sourceNode.id,
          name: sourceNode.data.name || 'HTTP Request',
          url: sourceNode.data.url || ''
        })
      }
    })
    
    return connectedRequests
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  // Toggle field expansion (accordion: only one field expanded at a time)
  const toggleFieldExpansion = (fieldId) => {
    setExpandedFields((prev) => {
      if (prev.has(fieldId)) {
        const next = new Set(prev)
        next.delete(fieldId)
        return next
      }
      return new Set([fieldId])
    })
  }

  /** Discrete options for "Value to match" when the controlling field is select/radio/checkbox. */
  const getConditionalMatchOptions = (sourceFieldId) => {
    const sourceField = formData.fields.find((f) => f.id === sourceFieldId)
    if (!sourceField) return null
    if (sourceField.type === "checkbox") {
      return [
        { value: "true", label: "Checked" },
        { value: "false", label: "Unchecked" },
      ]
    }
    if (
      (sourceField.type === "select" || sourceField.type === "radio") &&
      Array.isArray(sourceField.options) &&
      sourceField.options.length > 0
    ) {
      return sourceField.options.map((o) => ({
        value: o.value != null ? String(o.value) : "",
        label:
          (o.label != null ? String(o.label) : String(o.value ?? "")).trim() ||
          String(o.value ?? ""),
      }))
    }
    return null
  }

  // Validate value against step
  const validateStepValue = (value, step) => {
    if (value === '' || value === undefined || value === null) return true
    const numValue = parseFloat(value)
    if (isNaN(numValue)) return false
    
    // Check if the value is a multiple of the step
    const remainder = Math.abs(numValue % step)
    return remainder < 0.0001 || remainder > (step - 0.0001)
  }

  // Validate min/max relationship
  const validateMinMaxRelationship = (fieldId, minValue, maxValue) => {
    if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
      // If min > max, adjust min to max
      return { min: maxValue, max: maxValue }
    }
    return { min: minValue, max: maxValue }
  }

  // Validate default value against min/max range
  const validateDefaultValueRange = (fieldId, defaultValue, newMin = null, newMax = null) => {
    const field = formData.fields.find(f => f.id === fieldId)
    if (!field || field.type !== 'number') return defaultValue
    
    const minValue = newMin !== null ? newMin : field.validation?.min
    const maxValue = newMax !== null ? newMax : field.validation?.max
    
    if (!defaultValue) return defaultValue
    
    const numValue = parseFloat(defaultValue)
    if (isNaN(numValue)) return defaultValue
    
    let validatedValue = numValue
    
    // Clamp to min/max range
    if (minValue !== undefined && validatedValue < minValue) {
      validatedValue = minValue
    }
    if (maxValue !== undefined && validatedValue > maxValue) {
      validatedValue = maxValue
    }
    
    return validatedValue.toString()
  }

  // Check if default value should be updated when min/max changes
  const shouldUpdateDefaultValue = (fieldId, newMin, newMax, currentDefault = null) => {
    const field = formData.fields.find(f => f.id === fieldId)
    if (!field || field.type !== 'number') return false
    
    const defaultValue = currentDefault !== null ? currentDefault : field.defaultValue
    
    // If no default value and min is set, set default to min
    if (!defaultValue && newMin !== undefined) return true
    
    // Update if default is outside the new range
    if (defaultValue) {
      const numDefault = parseFloat(defaultValue)
      if (!isNaN(numDefault)) {
        if (newMin !== undefined && numDefault < newMin) return true
        if (newMax !== undefined && numDefault > newMax) return true
      }
    }
    
    return false
  }

  // Round value to nearest valid step
  const roundToStep = (value, step) => {
    if (value === '' || value === undefined || value === null) return value
    const numValue = parseFloat(value)
    if (isNaN(numValue)) return value
    
    return Math.round(numValue / step) * step
  }

  // Handle arrow button clicks for min/max inputs
  const handleArrowClick = (fieldId, inputType, direction) => {
    const field = formData.fields.find(f => f.id === fieldId)
    if (!field) return

    const currentValue = field.validation?.[inputType] || 0
    const step = field.validation?.step || 1
    let newValue = direction === 'up' ? currentValue + step : currentValue - step

    // Get the input element and update its value
    const inputKey = `${fieldId}-${inputType}`
    const inputElement = minInputRefs.current[inputKey]
    if (inputElement) {
      inputElement.value = newValue.toString()
    }

    // For min/max inputs, validate relationships and check default value
    let updateData = { 
      validation: { 
        ...field.validation, 
        [inputType]: newValue 
      } 
    }

    if (inputType === 'min' || inputType === 'max') {
      // Validate min/max relationship using the new value
      const minValue = inputType === 'min' ? newValue : field.validation?.min
      const maxValue = inputType === 'max' ? newValue : field.validation?.max
      const validation = validateMinMaxRelationship(fieldId, minValue, maxValue)
      
      // Update the validation values with the validated values
      updateData.validation = {
        ...field.validation,
        min: validation.min,
        max: validation.max
      }

      // Check if default value needs updating using the validated min/max values
      const currentDefault = field.defaultValue
      if (shouldUpdateDefaultValue(fieldId, validation.min, validation.max, currentDefault)) {
        const updatedDefault = validateDefaultValueRange(fieldId, currentDefault, validation.min, validation.max)
        if (updatedDefault !== currentDefault) {
          updateData.defaultValue = updatedDefault
        }
      }
    }

    // Update the state
    debouncedUpdateField(fieldId, updateData)
  }

  // Field type options
  const fieldTypes = [
    { value: "text", label: "Text Input", icon: "📝" },
    { value: "email", label: "Email", icon: "📧" },
    { value: "number", label: "Number", icon: "🔢" },
    { value: "textarea", label: "Text Area", icon: "📄" },
    { value: "select", label: "Dropdown", icon: "📋" },
    { value: "checkbox", label: "Checkbox", icon: "☑️" },
    { value: "radio", label: "Radio Buttons", icon: "🔘" },
    { value: "date", label: "Date", icon: "📅" },
    // { value: "file", label: "File Upload", icon: "📁" },
    // { value: "hidden", label: "Hidden Field", icon: "👻" }
  ]

  // Step value options
  const stepOptions = [
    { value: 1, label: "Whole Number (1)" },
    { value: 0.5, label: "Half (0.5)" },
    { value: 0.25, label: "Quarter (0.25)" }
  ]

  // Add new field
  const addField = () => {
    const newField = {
      id: `field-${Date.now()}`,
      type: "text",
      label: "New Field",
      placeholder: "Enter value",
      required: false,
      defaultValue: "",
      validation: {
        minLength: 0,
        maxLength: 100,
        pattern: "",
        step: 1
      },
      options: [], // For select/radio fields
      conditionalBehavior: normalizeConditionalBehavior(null),
    }
    
    const updatedData = {
      ...formData,
      fields: [...formData.fields, newField]
    }
    
    setFormData(updatedData)
    
    // Save immediately since this is an action
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, updatedData)
    }
  }

  // Remove field
  const removeField = (fieldId) => {
    const updatedData = {
      ...formData,
      fields: formData.fields.filter(field => field.id !== fieldId)
    }
    
    setFormData(updatedData)
    
    // Save immediately since this is an action
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, updatedData)
    }
  }


  // Update field (local state only — sync on blur via handleBlur for text inputs)
  const updateField = (fieldId, updates) => {
    setFormData((prev) => mergeFieldUpdates(prev, fieldId, updates))

    // Note: Do not call updateWorkflowContext on every keystroke — it updates global
    // workflow state and re-renders the entire canvas. Sync on blur via handleBlur.
  }

  // Add option to select/radio field
  const addOption = (fieldId) => {
    const updatedData = {
      ...formData,
      fields: formData.fields.map(field => 
        field.id === fieldId 
          ? { 
              ...field, 
              options: [...(field.options || []), { 
                id: `option-${Date.now()}`, 
                label: "New Option", 
                value: "new_option",
                valueManuallyEdited: false
              }] 
            }
          : field
      )
    }
    
    setFormData(updatedData)
    
    // Save immediately since this is an action
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, updatedData)
    }
  }

  // Remove option from select/radio field
  const removeOption = (fieldId, optionId) => {
    const updatedData = {
      ...formData,
      fields: formData.fields.map(field => 
        field.id === fieldId 
          ? { 
              ...field, 
              options: field.options.filter(option => option.id !== optionId) 
            }
          : field
      )
    }
    
    setFormData(updatedData)
    
    // Save immediately since this is an action
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, updatedData)
    }
  }

  // Update option
  const updateOption = (fieldId, optionId, updates) => {
    // Helper function to ensure string values are never null
    const ensureString = (value) => value === null || value === undefined ? "" : String(value)
    
    // Process updates to ensure string values are properly handled
    const processedUpdates = { ...updates }
    
    // Ensure string fields are never null
    if (processedUpdates.label !== undefined) {
      processedUpdates.label = ensureString(processedUpdates.label)
    }
    if (processedUpdates.value !== undefined) {
      processedUpdates.value = ensureString(processedUpdates.value)
    }
    
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        if (field.id !== fieldId) return field

        return {
          ...field,
          options: field.options.map((option) => {
            if (option.id !== optionId) return option

            const updatedOption = { ...option, ...processedUpdates }
            const currentlyManual = Boolean(option.valueManuallyEdited)

            if (processedUpdates.value !== undefined) {
              updatedOption.valueManuallyEdited = true
              return updatedOption
            }

            if (processedUpdates.label !== undefined) {
              if (!currentlyManual) {
                updatedOption.value = toSnakeCase(processedUpdates.label)
              }
              updatedOption.valueManuallyEdited = currentlyManual
            }

            return updatedOption
          }),
        }
      }),
    }))

    // Note: Sync workflow context on blur only (see handleBlur).
  }

  const handleOptionValueBlur = (fieldId, optionId) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        if (field.id !== fieldId) return field

        return {
          ...field,
          options: field.options.map((option) => {
            if (option.id !== optionId) return option

            const currentValue = option.value === null || option.value === undefined ? "" : String(option.value).trim()
            if (currentValue !== "") {
              return option
            }

            return {
              ...option,
              value: toSnakeCase(option.label),
              valueManuallyEdited: false,
            }
          }),
        }
      }),
    }))

    handleBlur("field")
  }

  // Handle form data changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Note: Sync workflow context on blur only (see handleBlur).
  }

  // Helper function to update workflow context with form data
  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      // Update the workflow context
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  const persistFieldUpdate = useCallback(
    (fieldId, updates) => {
      setFormData((prev) => {
        const updatedData = mergeFieldUpdates(prev, fieldId, updates)
        updateWorkflowContext(updatedData)
        if (data.onNodeDataChange) {
          data.onNodeDataChange(id, updatedData)
        }
        return updatedData
      })
    },
    [data.onNodeDataChange, id, updateWorkflowContext],
  )

  const handleDateFormatChange = (format) => {
    setFormData((prev) => {
      const updatedData = { ...prev, dynamicDateFormat: format }
      updateWorkflowContext(updatedData)
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, updatedData)
      }
      return updatedData
    })
  }

  // Handle drag end event for field reordering
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = formData.fields.findIndex((f) => f.id === active.id)
      const newIndex = formData.fields.findIndex((f) => f.id === over.id)

      const reorderedFields = arrayMove(formData.fields, oldIndex, newIndex)
      
      const updatedData = {
        ...formData,
        fields: reorderedFields,
      }

      setFormData(updatedData)
      updateWorkflowContext(updatedData)
      
      // Save to database immediately
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, updatedData)
      }
    }
  }

  // Debounced update field function
  const debouncedUpdateField = useCallback((fieldId, updates) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const ensureString = (value) =>
        value === null || value === undefined ? "" : String(value)

      const processedUpdates = { ...updates }

      if (processedUpdates.label !== undefined) {
        processedUpdates.label = ensureString(processedUpdates.label)
      }
      if (processedUpdates.placeholder !== undefined) {
        processedUpdates.placeholder = ensureString(processedUpdates.placeholder)
      }
      if (processedUpdates.defaultValue !== undefined) {
        processedUpdates.defaultValue = ensureString(processedUpdates.defaultValue)
      }
      if (processedUpdates.validation?.pattern !== undefined) {
        processedUpdates.validation = {
          ...processedUpdates.validation,
          pattern: ensureString(processedUpdates.validation.pattern),
        }
      }

      setFormData((prev) => ({
        ...prev,
        fields: prev.fields.map((field) =>
          field.id === fieldId ? { ...field, ...processedUpdates } : field,
        ),
      }))
      // Note: No updateWorkflowContext here — avoids full-canvas re-renders while typing.
    }, 100)
  }, [])

  // Update the ref whenever debouncedUpdateField changes
  useEffect(() => {
    debouncedUpdateFieldRef.current = debouncedUpdateField;
  }, [debouncedUpdateField]);

  // Handle blur events to save to database
  const handleBlur = () => {
    // Defer until after React commits any pending setState from the same blur event
    // (VariableReference calls onChange then onBlur synchronously).
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = formDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) persist(nodeId, current)
    }, 0)
  }

  // Handle settings changes
  const handleSettingChange = (setting, value) => {
    // Helper function to ensure string values are never null
    const ensureString = (value) => value === null || value === undefined ? "" : String(value)
    
    // Process the value to ensure string values are properly handled
    let processedValue = value
    if (typeof value === 'string' || value === null || value === undefined) {
      processedValue = ensureString(value)
    }
    
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [setting]: processedValue },
    }))
    // Note: Sync workflow context on blur only (see handleBlur).
  }

  // Initialize last input values and cleanup on unmount
  useEffect(() => {
    // Initialize last input values for all number fields
    formData.fields.forEach(field => {
      if (field.type === 'number') {
        lastInputValues.current[`${field.id}-min`] = field.validation?.min?.toString() || '';
        lastInputValues.current[`${field.id}-max`] = field.validation?.max?.toString() || '';
      }
    });
    
    
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (arrowKeyTimeoutRef.current) {
        clearTimeout(arrowKeyTimeoutRef.current);
      }
    };
  }, [formData.fields]);

  return (
    <div
      className={`trigger-node-container form-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "form-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Node Header */}
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <FileText size={16} className="trigger-icon" />
          <span>{formData.name && formData.name.trim() !== "" ? formData.name : "Dynamic Form"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons 
            nodeId={id}
            nodeType="formNode"
            nodeName={formData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          {/* <div className="square-button-container nodrag">
            <button
              className="trigger-node-button nodrag"
              onMouseEnter={() => setShowSquareTooltip(true)}
              onMouseLeave={() => setShowSquareTooltip(false)}
            >
              <Table2 size={16} />
            </button>
            {showSquareTooltip && <div className="square-tooltip">Open in tab</div>}
          </div> */}
          <button 
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm('Are you sure you want to delete this node?')) {
                try {
                  // Use the workflow context's deleteNode function which handles optimistic updates
                  if (deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    throw new Error('Unable to delete node: missing deleteNode function or currentWorkflow')
                  }
                } catch (error) {
                  console.error('Failed to delete node:', error)
                  alert('Failed to delete node. Please try again.')
                }
              }
            }}
            title="Delete node"
            style={{ color: '#ef4444' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Node Content */}
      {expanded && (
        <div 
          className="node-content nowheel"
          onWheel={(e) => {
            e.stopPropagation()
          }}
        >
          {/* Tabs */}
          <div className="form-tabs dark:border-border">
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "form" ? "active" : ""}`}
              onClick={() => setActiveTab("form")}
            >
              Form Fields
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </button>
            <button
              className={`tab-button dark:bg-[#1f2937] dark:text-white ${activeTab === "preview" ? "active" : ""}`}
              onClick={() => setActiveTab("preview")}
            >
              Preview
            </button>
          </div>

          {/* Form Fields Tab */}
          {activeTab === "form" && (
            <div className="form-tab-content">
              <div className="node-field">
                <label className="dark:text-white">Form Name</label>
                <EnhancedVariableReference
                  value={formData.name}
                  onChange={(value) => handleChange("name", value)}
                  onBlur={() => handleBlur("name")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Enter form name or use variables..."
                  className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus-within:ring-2 dark:focus-within:ring-blue-500 nodrag [&_.variable-input]:border-transparent [&_.variable-input]:bg-transparent [&_.variable-input]:shadow-none [&_.variable-input:focus]:!ring-0 [&_.variable-input:focus]:!outline-none [&_.variable-input:focus]:!border-transparent"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={formData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
                {/* Show connected request nodes */}
                {(() => {
                  const connectedRequests = getConnectedRequestNodes()
                  if (connectedRequests.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '4px', 
                        fontSize: '11px', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <ExternalLink size={10} />
                        <span>Connected to: {connectedRequests.map(req => req.name).join(', ')}</span>
                      </div>
                    )
                  }
                  return null
                })()}
              </div>

              <div className="node-field">
                <label className="dark:text-white">Description</label>
                <EnhancedVariableReference
                  value={formData.description}
                  onChange={(value) => handleChange("description", value)}
                  onBlur={() => handleBlur("description")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Describe this form..."
                  className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus-within:ring-2 dark:focus-within:ring-blue-500 nodrag [&_.variable-input]:border-transparent [&_.variable-input]:bg-transparent [&_.variable-input]:shadow-none [&_.variable-input:focus]:!ring-0 [&_.variable-input:focus]:!outline-none [&_.variable-input:focus]:!border-transparent"
                  showDateVariables={true}
                  showPreview={true}
                  dateFormat={formData.dynamicDateFormat}
                  onDateFormatChange={handleDateFormatChange}
                />
              </div>

              <div className="form-fields-section">
                <div className="section-header">
                  <label className="dark:text-white">Form Fields</label>
                  <button 
                    className="add-field-button"
                    onClick={addField}
                  >
                    <PlusIcon size={14} />
                    Add Field
                  </button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={formData.fields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="fields-list nodrag">
                      {formData.fields.map((field, index) => {
                        const isExpanded = expandedFields.has(field.id)
                        const fieldTitle = field.label && field.label.trim() !== "" ? field.label : `Field ${index + 1}`
                        
                        return (
                          <SortableFieldItem key={field.id} field={field} index={index}>
                            {(listeners) => (
                              <div className="field-item nodrag dark:bg-[#1f2937] dark:text-white dark:border-border">
                                <div
                                  className="field-header form-field-header-toggle dark:bg-[#1f2937] dark:text-white dark:border-border"
                                  onClick={(e) => {
                                    if (
                                      e.target.closest(".form-field-drag-handle") ||
                                      e.target.closest(".form-field-remove-btn")
                                    ) {
                                      return
                                    }
                                    toggleFieldExpansion(field.id)
                                  }}
                                >
                                  <div className="field-header-left">
                                    <div
                                      {...listeners}
                                      className="form-field-drag-handle nodrag"
                                      style={{ cursor: "grab", display: "flex", alignItems: "center" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <GripVertical size={20} className="drag-handle" />
                                    </div>
                                    <span className="field-number dark:text-white">
                                      #{index + 1}
                                    </span>
                                    <span className="field-title dark:text-white">
                                      {fieldTitle}
                                    </span>
                                  </div>
                          <div className="field-header-right">
                            <button
                              type="button"
                              className="form-field-remove-btn remove-field-button dark:hover:bg-[#1f2937] dark:hover:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation()
                                removeField(field.id)
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="field-content dark:bg-[#1f2937] dark:text-white dark:border-border">
                            <div className="field-section">
                              <div className="field-row">
                                <div className="field-input">
                                  <label className="dark:text-white">Field Type</label>
                                  <select
                                    value={field.type}
                                    title={field.type || ""}
                                    onChange={(e) => updateField(field.id, { type: e.target.value })}
                                    className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
                                  >
                                    {fieldTypes.map(type => (
                                      <option key={type.value} value={type.value}>
                                        {type.icon} {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="field-input">
                                  <label className="dark:text-white">Label</label>
                                  <VariableReference
                                    value={field.label}
                                    onChange={(value) => updateField(field.id, { label: value })}
                                    onBlur={() => handleBlur("field")}
                                    availableVariables={getAvailableVariables()}
                                    placeholder="Field label..."
                                    className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                                  />
                                </div>
                              </div>

                              <div className="field-row">
                                <div className="field-input">
                                  <label className="dark:text-white">Placeholder</label>
                                  <VariableReference
                                    value={field.placeholder}
                                    onChange={(value) => updateField(field.id, { placeholder: value })}
                                    onBlur={() => handleBlur("field")}
                                    availableVariables={getAvailableVariables()}
                                    placeholder="Placeholder text..."
                                    className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                                  />
                                </div>
                                {field.type !== 'date' && (
                                <div className="field-input">
                                  <label className="dark:text-white">Default Value</label>
                                  {field.type === 'number' ? (
                                    <div className="relative">
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={field.defaultValue || ''}
                                        title={String(field.defaultValue || "")}
                                        onChange={(e) => {
                                          const inputValue = e.target.value;
                                          
                                          // Only allow numeric input
                                          if (inputValue !== '' && !/^-?\d*\.?\d*$/.test(inputValue)) {
                                            e.target.value = field.defaultValue || '';
                                            return;
                                          }
                                          
                                          // Just update the value without validation during typing
                                          updateField(field.id, { defaultValue: inputValue });
                                        }}
                                        onBlur={(e) => {
                                          const inputValue = e.target.value;
                                          const step = field.validation?.step || 1;
                                          
                                          if (inputValue !== '') {
                                            let roundedValue = parseFloat(inputValue);
                                            
                                            // Round to step if needed
                                            if (!validateStepValue(inputValue, step)) {
                                              roundedValue = roundToStep(roundedValue, step);
                                            }
                                            
                                            // Validate against min/max range
                                            const rangeValidatedValue = validateDefaultValueRange(field.id, roundedValue.toString());
                                            
                                            e.target.value = rangeValidatedValue;
                                            updateField(field.id, { defaultValue: rangeValidatedValue });
                                          }
                                          
                                          handleBlur("field");
                                        }}
                                        placeholder="Default value..."
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag pr-6"
                                      />
                                      <div className="absolute right-0.5 top-1/2 transform -translate-y-1/2 flex flex-col">
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => {
                                            const currentValue = parseFloat(field.defaultValue) || 0
                                            const step = field.validation?.step || 1
                                            const newValue = currentValue + step
                                            const rangeValidatedValue = validateDefaultValueRange(field.id, newValue.toString())
                                            updateField(field.id, { defaultValue: rangeValidatedValue })
                                          }}
                                        >
                                          <ArrowUp size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => {
                                            const currentValue = parseFloat(field.defaultValue) || 0
                                            const step = field.validation?.step || 1
                                            const newValue = currentValue - step
                                            const rangeValidatedValue = validateDefaultValueRange(field.id, newValue.toString())
                                            updateField(field.id, { defaultValue: rangeValidatedValue })
                                          }}
                                        >
                                          <ArrowDown size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <VariableReference
                                      value={field.defaultValue}
                                      onChange={(value) => updateField(field.id, { defaultValue: value })}
                                      onBlur={() => handleBlur("field")}
                                      availableVariables={getAvailableVariables()}
                                      placeholder="Default value..."
                                      className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                                    />
                                  )}
                                </div>
                                )}
                              </div>

                              {field.type === 'date' && (
                                <div className="flex flex-col gap-3 mt-1">
                                  <FormDateExpressionEditor
                                    label="Default Value"
                                    value={field.defaultValue || ""}
                                    offset={field.defaultValueOffset}
                                    availableVariables={getAvailableVariables()}
                                    dateFormat={formData.dynamicDateFormat}
                                    onChange={({ value, offset: nextOffset }) => {
                                      const updates = { defaultValue: value }
                                      if (nextOffset) {
                                        updates.defaultValueOffset = nextOffset
                                      } else {
                                        updates.defaultValueOffset = null
                                      }
                                      persistFieldUpdate(field.id, updates)
                                    }}
                                    onBlur={() => handleBlur("field")}
                                  />
                                  <FormDateExpressionEditor
                                    label="Minimum Date"
                                    value={field.validation?.minDate || ""}
                                    offset={field.validation?.minDateOffset}
                                    availableVariables={getAvailableVariables()}
                                    dateFormat={formData.dynamicDateFormat}
                                    onChange={({ value, offset: nextOffset }) => {
                                      const validation = { ...field.validation }
                                      if (value) validation.minDate = value
                                      else delete validation.minDate
                                      if (nextOffset) validation.minDateOffset = nextOffset
                                      else delete validation.minDateOffset
                                      persistFieldUpdate(field.id, { validation })
                                    }}
                                    onBlur={() => handleBlur("field")}
                                  />
                                  <FormDateExpressionEditor
                                    label="Maximum Date"
                                    value={field.validation?.maxDate || ""}
                                    offset={field.validation?.maxDateOffset}
                                    availableVariables={getAvailableVariables()}
                                    dateFormat={formData.dynamicDateFormat}
                                    onChange={({ value, offset: nextOffset }) => {
                                      const validation = { ...field.validation }
                                      if (value) validation.maxDate = value
                                      else delete validation.maxDate
                                      if (nextOffset) validation.maxDateOffset = nextOffset
                                      else delete validation.maxDateOffset
                                      persistFieldUpdate(field.id, { validation })
                                    }}
                                    onBlur={() => handleBlur("field")}
                                  />
                                </div>
                              )}

                              {/* Min/Max validation for number fields */}
                              {field.type === 'number' && (
                                <>
                                  <div className="flex flex-col gap-2">
                                    <div className="field-input">
                                      <label className="dark:text-white">Step Value</label>
                                      <select
                                        value={field.validation?.step || 1}
                                        title={String(field.validation?.step || 1)}
                                        onChange={(e) => {
                                          const newStep = parseFloat(e.target.value)
                                          updateField(field.id, { 
                                            validation: { 
                                              ...field.validation, 
                                              step: newStep 
                                            } 
                                          })
                                          // Save immediately since this is a select action
                                          if (data.onNodeDataChange) {
                                            const updatedData = {
                                              ...formData,
                                              fields: formData.fields.map(f => 
                                                f.id === field.id ? { 
                                                  ...f, 
                                                  validation: { 
                                                    ...f.validation, 
                                                    step: newStep 
                                                  } 
                                                } : f
                                              )
                                            }
                                            data.onNodeDataChange(id, updatedData)
                                          }
                                        }}
                                        className="dark:bg-[#1f2937] dark:text-white"
                                      >
                                        {stepOptions.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="field-row">
                                    <div className="field-input">
                                      <label className="dark:text-white">Minimum Value</label>
                                    <div className="relative">
                                      <input
                                        ref={(el) => {
                                          if (el) {
                                            minInputRefs.current[`${field.id}-min`] = el;
                                          }
                                        }}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={field.validation?.min || ''}
                                        title={String(field.validation?.min ?? "")}
                                        data-field-id={field.id}
                                        data-input-key={`${field.id}-min`}
                                        data-input-type="min"
                                        onKeyDown={(e) => {
                                          // Handle arrow keys manually
                                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            const currentValue = parseFloat(e.target.value) || 0;
                                            const step = 1;
                                            const newValue = e.key === 'ArrowUp' ? currentValue + step : currentValue - step;
                                            
                                            // Update the input value directly
                                            e.target.value = newValue.toString();
                                            
                                            // Update the state
                                            const value = newValue;
                                            debouncedUpdateField(field.id, { 
                                              validation: { 
                                                ...field.validation, 
                                                min: value 
                                              } 
                                            });
                                          }
                                        }}
                                        onChange={(e) => {
                                          // Skip if arrow key is being processed
                                          if (isArrowKeyPressed.current) {
                                            return;
                                          }
                                          
                                          const inputValue = e.target.value;
                                          const inputKey = `${field.id}-min`;
                                          const step = field.validation?.step || 1;
                                          
                                          // Only allow numeric input
                                          if (inputValue !== '' && !/^-?\d*\.?\d*$/.test(inputValue)) {
                                            e.target.value = lastInputValues.current[inputKey] || '';
                                            return;
                                          }
                                          
                                          // Check if value actually changed
                                          if (lastInputValues.current[inputKey] === inputValue) {
                                            return;
                                          }
                                          
                                          // Just update the value without validation during typing
                                          
                                          // Update last value
                                          lastInputValues.current[inputKey] = inputValue;
                                          
                                          const value = inputValue === '' ? undefined : parseFloat(inputValue)
                                          debouncedUpdateField(field.id, { 
                                            validation: { 
                                              ...field.validation, 
                                              min: value 
                                            } 
                                          })
                                        }}
                                        onWheel={(e) => {
                                          // Prevent mouse wheel from changing the value
                                          e.preventDefault();
                                        }}
                                        onFocus={(e) => {
                                          // Focus handler
                                        }}
                                        onBlur={(e) => {
                                          // Reset arrow key flag on blur
                                          isArrowKeyPressed.current = false;
                                          
                                          // Validate and round to step on blur
                                          const inputValue = e.target.value;
                                          const step = field.validation?.step || 1;
                                          
                                          if (inputValue !== '') {
                                            let roundedValue = parseFloat(inputValue);
                                            
                                            // Round to step if needed
                                            if (!validateStepValue(inputValue, step)) {
                                              roundedValue = roundToStep(roundedValue, step);
                                            }
                                            
                                            // Validate min/max relationship
                                            const maxValue = field.validation?.max;
                                            const validation = validateMinMaxRelationship(field.id, roundedValue, maxValue);
                                            
                                            e.target.value = validation.min.toString();
                                            lastInputValues.current[`${field.id}-min`] = validation.min.toString();
                                            
                                            // Check if default value needs updating
                                            let defaultUpdate = {}
                                            const currentDefault = field.defaultValue
                                            if (shouldUpdateDefaultValue(field.id, validation.min, validation.max, currentDefault)) {
                                              const updatedDefault = validateDefaultValueRange(field.id, currentDefault, validation.min, validation.max)
                                              if (updatedDefault !== currentDefault) {
                                                defaultUpdate = { defaultValue: updatedDefault }
                                              }
                                            }
                                            
                                            // Update both min and max if they were adjusted, plus default if needed
                                            debouncedUpdateField(field.id, { 
                                              validation: { 
                                                ...field.validation, 
                                                min: validation.min,
                                                max: validation.max
                                              },
                                              ...defaultUpdate
                                            });
                                          }
                                          
                                          handleBlur("field");
                                        }}
                                        placeholder="No minimum"
                                        min="-999999999"
                                        max="999999999"
                                        step="1"
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag pr-6"
                                      />
                                      <div className="absolute right-0.5 top-1/2 transform -translate-y-1/2 flex flex-col">
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => handleArrowClick(field.id, 'min', 'up')}
                                        >
                                          <ArrowUp size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => handleArrowClick(field.id, 'min', 'down')}
                                        >
                                          <ArrowDown size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="field-input">
                                    <label className="dark:text-white">Maximum Value</label>
                                    <div className="relative">
                                      <input
                                        ref={(el) => {
                                          if (el) {
                                            minInputRefs.current[`${field.id}-max`] = el;
                                          }
                                        }}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={field.validation?.max || ''}
                                        title={String(field.validation?.max ?? "")}
                                        data-field-id={field.id}
                                        data-input-key={`${field.id}-max`}
                                        data-input-type="max"
                                        onKeyDown={(e) => {
                                          // Handle arrow keys manually
                                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            
                                            const currentValue = parseFloat(e.target.value) || 0;
                                            const step = 1;
                                            const newValue = e.key === 'ArrowUp' ? currentValue + step : currentValue - step;
                                            
                                            // Update the input value directly
                                            e.target.value = newValue.toString();
                                            
                                            // Update the state
                                            const value = newValue;
                                            debouncedUpdateField(field.id, { 
                                              validation: { 
                                                ...field.validation, 
                                                max: value 
                                              } 
                                            });
                                          }
                                        }}
                                        onChange={(e) => {
                                          // Skip if arrow key is being processed
                                          if (isArrowKeyPressed.current) {
                                            return;
                                          }
                                          
                                          const inputValue = e.target.value;
                                          const inputKey = `${field.id}-max`;
                                          const step = field.validation?.step || 1;
                                          
                                          // Only allow numeric input
                                          if (inputValue !== '' && !/^-?\d*\.?\d*$/.test(inputValue)) {
                                            e.target.value = lastInputValues.current[inputKey] || '';
                                            return;
                                          }
                                          
                                          // Check if value actually changed
                                          if (lastInputValues.current[inputKey] === inputValue) {
                                            return;
                                          }
                                          
                                          // Just update the value without validation during typing
                                          
                                          // Update last value
                                          lastInputValues.current[inputKey] = inputValue;
                                          
                                          const value = inputValue === '' ? undefined : parseFloat(inputValue)
                                          debouncedUpdateField(field.id, { 
                                            validation: { 
                                              ...field.validation, 
                                              max: value 
                                            } 
                                          })
                                        }}
                                        onWheel={(e) => {
                                          // Prevent mouse wheel from changing the value
                                          e.preventDefault();
                                        }}
                                        onFocus={(e) => {
                                          // Focus handler
                                        }}
                                        onBlur={(e) => {
                                          // Reset arrow key flag on blur
                                          isArrowKeyPressed.current = false;
                                          
                                          // Validate and round to step on blur
                                          const inputValue = e.target.value;
                                          const step = field.validation?.step || 1;
                                          
                                          if (inputValue !== '') {
                                            let roundedValue = parseFloat(inputValue);
                                            
                                            // Round to step if needed
                                            if (!validateStepValue(inputValue, step)) {
                                              roundedValue = roundToStep(roundedValue, step);
                                            }
                                            
                                            // Validate min/max relationship
                                            const minValue = field.validation?.min;
                                            const validation = validateMinMaxRelationship(field.id, minValue, roundedValue);
                                            
                                            e.target.value = validation.max.toString();
                                            lastInputValues.current[`${field.id}-max`] = validation.max.toString();
                                            
                                            // Check if default value needs updating
                                            let defaultUpdate = {}
                                            const currentDefault = field.defaultValue
                                            if (shouldUpdateDefaultValue(field.id, validation.min, validation.max, currentDefault)) {
                                              const updatedDefault = validateDefaultValueRange(field.id, currentDefault, validation.min, validation.max)
                                              if (updatedDefault !== currentDefault) {
                                                defaultUpdate = { defaultValue: updatedDefault }
                                              }
                                            }
                                            
                                            // Update both min and max if they were adjusted, plus default if needed
                                            debouncedUpdateField(field.id, { 
                                              validation: { 
                                                ...field.validation, 
                                                min: validation.min,
                                                max: validation.max
                                              },
                                              ...defaultUpdate
                                            });
                                          }
                                          
                                          handleBlur("field");
                                        }}
                                        placeholder="No maximum"
                                        min="-999999999"
                                        max="999999999"
                                        step="1"
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag pr-6"
                                      />
                                      <div className="absolute right-0.5 top-1/2 transform -translate-y-1/2 flex flex-col">
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => handleArrowClick(field.id, 'max', 'up')}
                                        >
                                          <ArrowUp size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                        <button
                                          type="button"
                                          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-sm flex items-center justify-center"
                                          onClick={() => handleArrowClick(field.id, 'max', 'down')}
                                        >
                                          <ArrowDown size={10} className="text-gray-500 dark:text-gray-400" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                </>
                              )}

                              <div className="field-row">
                                <div className="field-checkbox">
                                  <label className="dark:text-white">
                                    <input
                                      type="checkbox"
                                      checked={field.required}
                                      onChange={(e) => {
                                        persistFieldUpdate(field.id, {
                                          required: e.target.checked,
                                        })
                                      }}
                                    />
                                    Required field
                                  </label>
                                </div>
                              </div>

                              {/* Options for select/radio fields */}
                              {(field.type === 'select' || field.type === 'radio') && (
                                <div className="field-options dark:bg-[#1f2937] dark:text-white dark:border-border">
                                  <div className="options-header">
                                    <label className="dark:text-white">Options</label>
                                    <button
                                      className="add-option-button"
                                      onClick={() => addOption(field.id)}
                                    >
                                      <PlusIcon size={12} />
                                      Add Option
                                    </button>
                                  </div>
                                  <div className="options-list">
                                    {(field.options || []).map((option, optionIndex) => (
                                      <div key={option.id} className="option-item">
                                        <div className="option-item-row">
                                          <VariableReference
                                            value={option.label}
                                            onChange={(value) => updateOption(field.id, option.id, { label: value })}
                                            onBlur={() => handleBlur("field")}
                                            availableVariables={getAvailableVariables()}
                                            placeholder="Option label"
                                            className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                                          />
                                          <VariableReference
                                            value={option.value}
                                            onChange={(value) => updateOption(field.id, option.id, { value: value })}
                                            onBlur={() => handleOptionValueBlur(field.id, option.id)}
                                            availableVariables={getAvailableVariables()}
                                            placeholder="Option value"
                                            className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500"
                                          />
                                          <button
                                            className="remove-option-button"
                                            onClick={() => removeOption(field.id, option.id)}
                                          >
                                            <XCircle size={12} />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="field-section border-t border-gray-200 dark:border-gray-600 pt-3 mt-3 space-y-2">
                                <div className="text-sm font-medium dark:text-white">Conditional logic</div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                                  Case-insensitive. <strong>Value to match</strong> is a picker for
                                  dropdown/radio/checkbox, otherwise type a value. <strong>Visibility</strong> controls
                                  when this field appears relative to the condition.
                                </p>
                                <div className="field-checkbox">
                                  <label className="dark:text-white text-sm">
                                    <input
                                      type="checkbox"
                                      className="mr-2 align-middle"
                                      checked={Boolean(field.conditionalBehavior?.enabled)}
                                      onChange={(e) => {
                                        const next = normalizeConditionalBehavior({
                                          ...field.conditionalBehavior,
                                          enabled: e.target.checked,
                                        })
                                        persistFieldUpdate(field.id, {
                                          conditionalBehavior: next,
                                        })
                                      }}
                                    />
                                    Use conditional rules
                                  </label>
                                </div>
                                {field.conditionalBehavior?.enabled && (
                                  <>
                                    <div className="field-input">
                                      <label className="dark:text-white text-xs">When field</label>
                                      <select
                                        value={field.conditionalBehavior.sourceFieldId || ""}
                                        title={field.conditionalBehavior.sourceFieldId || ""}
                                        onChange={(e) => {
                                          const next = normalizeConditionalBehavior({
                                            ...field.conditionalBehavior,
                                            sourceFieldId: e.target.value,
                                            value: "",
                                          })
                                          persistFieldUpdate(field.id, {
                                            conditionalBehavior: next,
                                          })
                                        }}
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
                                      >
                                        <option value="">Select controlling field…</option>
                                        {formData.fields
                                          .filter((f) => f.id !== field.id)
                                          .map((f) => (
                                            <option key={f.id} value={f.id}>
                                              {(f.label && String(f.label).trim()) || f.id}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div className="field-input">
                                      <label className="dark:text-white text-xs">Operator</label>
                                      <select
                                        value={field.conditionalBehavior.operator || "equals"}
                                        title={field.conditionalBehavior.operator || "equals"}
                                        onChange={(e) => {
                                          const next = normalizeConditionalBehavior({
                                            ...field.conditionalBehavior,
                                            operator: e.target.value,
                                          })
                                          persistFieldUpdate(field.id, {
                                            conditionalBehavior: next,
                                          })
                                        }}
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
                                      >
                                        <option value="equals">Equals</option>
                                        <option value="not_equals">Does not equal</option>
                                      </select>
                                    </div>
                                    <div className="field-input">
                                      <label className="dark:text-white text-xs">Value to match</label>
                                      {(() => {
                                        const sourceId =
                                          field.conditionalBehavior.sourceFieldId
                                        const matchOptions =
                                          getConditionalMatchOptionsFromFields(
                                            formData.fields,
                                            sourceId,
                                          )
                                        const currentVal =
                                          field.conditionalBehavior.value ?? ""
                                        const currentStr = String(currentVal)

                                        if (matchOptions) {
                                          const known = new Set(
                                            matchOptions.map((o) => o.value),
                                          )
                                          const orphan =
                                            currentStr !== "" &&
                                            !known.has(currentStr)

                                          return (
                                            <select
                                              value={currentStr}
                                              title={currentStr}
                                              onChange={(e) => {
                                                const next =
                                                  normalizeConditionalBehavior({
                                                    ...field.conditionalBehavior,
                                                    value: e.target.value,
                                                  })
                                                persistFieldUpdate(field.id, {
                                                  conditionalBehavior: next,
                                                })
                                              }}
                                              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
                                            >
                                              <option value="">
                                                Select value…
                                              </option>
                                              {orphan && (
                                                <option value={currentStr}>
                                                  {currentStr} (custom / saved)
                                                </option>
                                              )}
                                              {matchOptions.map((o, idx) => (
                                                <option
                                                  key={`${o.value}-${idx}`}
                                                  value={o.value}
                                                >
                                                  {o.label !== o.value
                                                    ? `${o.label} — ${o.value}`
                                                    : o.value || "(empty)"}
                                                </option>
                                              ))}
                                            </select>
                                          )
                                        }

                                        return (
                                          <input
                                            type="text"
                                            value={currentStr}
                                            title={currentStr}
                                            onChange={(e) => {
                                              const next =
                                                normalizeConditionalBehavior({
                                                  ...field.conditionalBehavior,
                                                  value: e.target.value,
                                                })
                                              updateField(field.id, {
                                                conditionalBehavior: next,
                                              })
                                            }}
                                            onBlur={() => handleBlur("field")}
                                            placeholder='e.g. text or date value'
                                            className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
                                          />
                                        )
                                      })()}
                                    </div>
                                    <div className="field-input">
                                      <label className="dark:text-white text-xs">Visibility</label>
                                      <select
                                        value={conditionalVisibilityMode(
                                          field.conditionalBehavior,
                                        )}
                                        onChange={(e) => {
                                          const next =
                                            conditionalBehaviorWithVisibilityMode(
                                              field.conditionalBehavior,
                                              e.target.value,
                                            )
                                          persistFieldUpdate(field.id, {
                                            conditionalBehavior: next,
                                          })
                                        }}
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
                                      >
                                        <option value="always">Always visible</option>
                                        <option value="hide_when">
                                          Hide when condition is met
                                        </option>
                                        <option value="show_when">
                                          Show only when condition is met
                                        </option>
                                      </select>
                                    </div>
                                    <div className="field-checkbox">
                                      <label className="dark:text-white text-sm">
                                        <input
                                          type="checkbox"
                                          className="mr-2 align-middle"
                                          checked={Boolean(
                                            field.conditionalBehavior.requireWhenMatch,
                                          )}
                                          onChange={(e) => {
                                            const next = normalizeConditionalBehavior({
                                              ...field.conditionalBehavior,
                                              requireWhenMatch: e.target.checked,
                                            })
                                            persistFieldUpdate(field.id, {
                                              conditionalBehavior: next,
                                            })
                                          }}
                                        />
                                        Require this field when the condition is met
                                      </label>
                                    </div>
                                  </>
                                )}
                              </div>

                            </div>
                          </div>
                        )}
                              </div>
                            )}
                          </SortableFieldItem>
                        )
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="settings-tab-content">
              <div className="node-field">
                <label className="dark:text-white">Submit Button Text</label>
                <VariableReference
                  value={formData.settings.submitButtonText}
                  onChange={(value) => handleSettingChange("submitButtonText", value)}
                  onBlur={() => handleBlur("settings")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Submit"
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Reset Button Text</label>
                <VariableReference
                  value={formData.settings.resetButtonText}
                  onChange={(value) => handleSettingChange("resetButtonText", value)}
                  onBlur={() => handleBlur("settings")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Reset"
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Success Message</label>
                <VariableReference
                  value={formData.settings.successMessage}
                  onChange={(value) => handleSettingChange("successMessage", value)}
                  onBlur={() => handleBlur("settings")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Form submitted successfully!"
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Error Message</label>
                <VariableReference
                  value={formData.settings.errorMessage}
                  onChange={(value) => handleSettingChange("errorMessage", value)}
                  onBlur={() => handleBlur("settings")}
                  availableVariables={getAvailableVariables()}
                  placeholder="Please check your input and try again."
                />
              </div>

              <div className="node-field">
                <label className="dark:text-white">Redirect URL (optional)</label>
                <VariableReference
                  value={formData.settings.redirectUrl}
                  onChange={(value) => handleSettingChange("redirectUrl", value)}
                  onBlur={() => handleBlur("settings")}
                  availableVariables={getAvailableVariables()}
                  placeholder="https://example.com/success"
                />
              </div>

              <div className="node-field">
                <label style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }} className="dark:text-white">
                                      <input
                      type="checkbox"
                      checked={formData.settings.showProgressBar}
                      onChange={(e) => {
                        handleSettingChange("showProgressBar", e.target.checked)
                        // Save immediately since this is a checkbox action
                        if (data.onNodeDataChange) {
                          const updatedData = {
                            ...formData,
                            settings: { ...formData.settings, showProgressBar: e.target.checked }
                          }
                          data.onNodeDataChange(id, updatedData)
                        }
                      }}
                      style={{ marginRight: 8, width: 16, height: 16 }}
                    />
                  Show progress bar
                </label>
              </div>

              <div className="node-field">
                <label style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }} className="dark:text-white">
                                      <input
                      type="checkbox"
                      checked={formData.settings.allowMultipleSubmissions}
                      onChange={(e) => {
                        handleSettingChange("allowMultipleSubmissions", e.target.checked)
                        // Save immediately since this is a checkbox action
                        if (data.onNodeDataChange) {
                          const updatedData = {
                            ...formData,
                            settings: { ...formData.settings, allowMultipleSubmissions: e.target.checked }
                          }
                          data.onNodeDataChange(id, updatedData)
                        }
                      }}
                      style={{ marginRight: 8, width: 16, height: 16 }}
                    />
                  Allow multiple submissions
                </label>
              </div>

              <div className="node-field">
                <label style={{ display: "flex", alignItems: "center", fontWeight: 400, fontSize: 15 }} className="dark:text-white">
                  <input
                    type="checkbox"
                    checked={formData.settings.calculateNumberFields}
                    onChange={(e) => {
                      handleSettingChange("calculateNumberFields", e.target.checked)
                      // Save immediately since this is a checkbox action
                      if (data.onNodeDataChange) {
                        const updatedData = {
                          ...formData,
                          settings: { ...formData.settings, calculateNumberFields: e.target.checked }
                        }
                        data.onNodeDataChange(id, updatedData)
                      }
                    }}
                    style={{ marginRight: 8, width: 16, height: 16 }}
                  />
                  Calculate number fields automatically
                </label>
                <div style={{ 
                  marginTop: '4px', 
                  fontSize: '12px', 
                  color: '#6b7280',
                  fontStyle: 'italic'
                }} className="dark:text-gray-400">
                  When enabled, number fields will be automatically summed and displayed
                </div>
              </div>

              {formData.settings.calculateNumberFields && (
                <div className="node-field">
                  <label className="dark:text-white">Total Calculation Name</label>
                  <EnhancedVariableReference
                    value={formData.settings.totalCalculationName}
                    onChange={(value) => handleSettingChange("totalCalculationName", value)}
                    onBlur={() => handleBlur("settings")}
                    availableVariables={getAvailableVariables()}
                    placeholder="Total Calculation"
                    className="w-full"
                    showDateVariables={true}
                    showPreview={true}
                    dateFormat={formData.dynamicDateFormat}
                    onDateFormatChange={handleDateFormatChange}
                  />
                  <div style={{ 
                    marginTop: '4px', 
                    fontSize: '12px', 
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }} className="dark:text-gray-400">
                    Customize the name displayed for the total calculation
                  </div>
                </div>
              )}

              {/* Available Variables Section */}
              {(() => {
                const availableVariables = getAvailableVariables()
                if (availableVariables.length > 0) {
                  return (
                    <div className="node-field">
                      <label className="dark:text-white">Available Variables</label>
                      <div className="variables-container" style={{
                        padding: "8px 12px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "12px"
                      }}>
                        <div style={{ marginBottom: "6px", fontWeight: "500" }} className="dark:text-white">
                          Variables from connected request nodes:
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {availableVariables.map((variable, index) => (
                            <span
                              key={index}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 6px",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "500"
                              }}
                              title={`${variable.name}: ${variable.value}`}
                            >
                              {variable.name}
                            </span>
                          ))}
                        </div>
                        <div style={{ 
                          marginTop: "6px", 
                          fontSize: "11px", 
                          fontStyle: "italic"
                        }}>
                          Use these variables in any field above by typing ~variable_name~
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === "preview" && (
            <div className="preview-tab-content">
              <div className="form-preview dark:bg-[#1f2937] dark:text-white dark:border-border">
                <h4 className="dark:text-white">Form Preview</h4>
                <div className="preview-form dark:bg-[#1f2937] dark:text-white dark:border-border">
                  <h3 className="dark:text-white">{formData.name || "Dynamic Form"}</h3>
                  {formData.description && <p>{formData.description}</p>}
                  
                  {formData.fields.map((field, index) => (
                    <div key={field.id} className="preview-field dark:bg-[#1f2937] dark:text-white dark:border-border">
                      <label className="dark:text-white">
                        {field.label || `Field ${index + 1}`}
                        {field.required && <span className="required">*</span>}
                      </label>
                      
                      {field.type === 'text' && (
                        <input
                          type="text"
                          placeholder={field.placeholder}
                          defaultValue={field.defaultValue}
                          disabled
                          className="dark:bg-[#1f2937] dark:text-white"
                        />
                      )}
                      
                      {field.type === 'email' && (
                        <input
                          type="email"
                          placeholder={field.placeholder}
                          defaultValue={field.defaultValue}
                          disabled
                          className="dark:bg-[#1f2937] dark:text-white"
                        />
                      )}
                      
                      {field.type === 'number' && (
                        <input
                          type="number"
                          placeholder={field.placeholder}
                          defaultValue={field.defaultValue}
                          disabled
                          className="dark:bg-[#1f2937] dark:text-white"
                        />
                      )}
                      
                      {field.type === 'textarea' && (
                        <textarea
                          placeholder={field.placeholder}
                          defaultValue={field.defaultValue}
                          rows={3}
                          disabled
                          className="dark:bg-[#1f2937] dark:text-white"
                        />
                      )}
                      
                      {field.type === 'select' && (
                        <select disabled className="dark:bg-[#1f2937] dark:text-white">
                          <option value="">{field.placeholder || "Select an option"}</option>
                          {(field.options || []).map(option => (
                            <option key={option.id} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                      
                      {field.type === 'checkbox' && (
                        <label className="checkbox-label dark:text-white">
                          <input type="checkbox" disabled className="dark:bg-[#1f2937] dark:text-white" />
                          {field.label}
                        </label>
                      )}
                      
                      {field.type === 'radio' && (
                        <div className="radio-group">
                          {(field.options || []).map(option => (
                            <label key={option.id} className="radio-label dark:text-white">
                              <input type="radio" name={`field-${field.id}`} disabled className="dark:bg-[#1f2937] dark:text-white" />
                              {option.label}
                            </label>
                          ))}
                        </div>
                      )}
                      
                      {field.type === 'date' && (
                        <input
                          type="date"
                          defaultValue={
                            /^\d{4}-\d{2}-\d{2}$/.test(String(field.defaultValue || "").trim())
                              ? String(field.defaultValue).trim()
                              : ""
                          }
                          min={
                            /^\d{4}-\d{2}-\d{2}$/.test(String(field.validation?.minDate || "").trim())
                              ? String(field.validation.minDate).trim()
                              : undefined
                          }
                          max={
                            /^\d{4}-\d{2}-\d{2}$/.test(String(field.validation?.maxDate || "").trim())
                              ? String(field.validation.maxDate).trim()
                              : undefined
                          }
                          disabled
                          className="cursor-pointer dark:bg-[#1f2937] dark:text-white disabled:cursor-not-allowed"
                        />
                      )}
                    </div>
                  ))}
                  
                  <div className="preview-buttons dark:bg-[#1f2937] dark:text-white dark:border-border">
                    <button className="preview-submit" disabled>
                      {formData.settings.submitButtonText || "Submit"}
                    </button>
                    <button className="preview-reset" disabled>
                      {formData.settings.resetButtonText || "Reset"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        // position={data.handlePosition === 'top-bottom' ? Position.Top : Position.Left}
        position={Position.Top}
        id="b"
      />

      {/* Output handle */}
      <Handle
        type="source"
        // position={data.handlePosition === 'top-bottom' ? Position.Bottom : Position.Right}
        position={Position.Bottom}
        id="a"
      />
    </div>
  )
}

export default FormNode 